class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.stickySubmit = this.querySelector('[data-sticky-atc-submit]');
    this.stickyButtonText = this.querySelector('[data-sticky-atc-button-text]');
    this.stickyImage = this.querySelector('[data-sticky-atc-image]');

    if (!this.mainSubmit || !this.stickySubmit) {
      this.remove();
      return;
    }

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.onStickySubmit = this.handleStickySubmit.bind(this);
    this.onScrollOrResize = this.updateVisibility.bind(this);
    this.onJudgeMeLoaded = this.syncRatingVisibility.bind(this);

    this.stickySubmit.addEventListener('click', this.onStickySubmit);
    window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
    window.addEventListener('resize', this.onScrollOrResize);
    window.addEventListener('judgeme:loaded', this.onJudgeMeLoaded);

    this.setupVisibilityObserver();
    this.setupMainButtonObserver();
    this.setupRatingObserver();
    this.syncFromMainButton();
    this.syncRatingVisibility();

    requestAnimationFrame(() => this.updateVisibility());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data?.sectionId !== this.sectionId) return;
        this.handleVariantChange(event.data.variant);
      });

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.syncLoadingState();
      });

      this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
        this.syncLoadingState();
      });
    }
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.mainButtonObserver?.disconnect();
    this.ratingObserver?.disconnect();
    this.stickySubmit?.removeEventListener('click', this.onStickySubmit);
    window.removeEventListener('scroll', this.onScrollOrResize);
    window.removeEventListener('resize', this.onScrollOrResize);
    window.removeEventListener('judgeme:loaded', this.onJudgeMeLoaded);
    this.variantChangeUnsubscriber?.();
    this.cartUpdateUnsubscriber?.();
    this.cartErrorUnsubscriber?.();
  }

  setupVisibilityObserver() {
    this.visibilityObserver = new IntersectionObserver(
      () => this.updateVisibility(),
      { threshold: [0, 1] }
    );

    this.visibilityObserver.observe(this.mainSubmit);
  }

  setupMainButtonObserver() {
    this.mainButtonObserver = new MutationObserver(() => {
      this.syncFromMainButton();
    });

    this.mainButtonObserver.observe(this.mainSubmit, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
    });
  }

  setupRatingObserver() {
    const ratingRoots = this.querySelectorAll('[data-sticky-atc-rating]');
    if (!ratingRoots.length) return;

    this.ratingObserver = new MutationObserver(() => {
      this.syncRatingVisibility();
    });

    ratingRoots.forEach((root) => {
      this.ratingObserver.observe(root, {
        attributes: true,
        attributeFilter: ['data-average-rating', 'data-number-of-reviews'],
        childList: true,
        subtree: true,
      });
    });

    this.observePageJudgeMeBadge();
  }

  observePageJudgeMeBadge() {
    if (!this.ratingObserver || this.pageBadgeObserved) return;

    const pageBadge = document.querySelector(
      '.product__title-row .jdgm-prev-badge, .product__info-container .jdgm-prev-badge'
    );
    if (!pageBadge) return;

    this.ratingObserver.observe(pageBadge, {
      attributes: true,
      attributeFilter: ['data-average-rating', 'data-number-of-reviews'],
    });
    this.pageBadgeObserved = true;
  }

  hasValidJudgeMeReviews() {
    // Only trust the live product-page Judge.me badge (metafields can be stale)
    const pageBadge = document.querySelector(
      '.product__title-row .jdgm-prev-badge, .product__info-container .jdgm-prev-badge'
    );
    if (!pageBadge) return false;

    const average = pageBadge.getAttribute('data-average-rating');
    const count = Number(pageBadge.getAttribute('data-number-of-reviews') || 0);

    if (average === '0.00' || average === '0' || count <= 0) return false;
    return true;
  }

  syncRatingVisibility() {
    const ratingBlocks = this.querySelectorAll('[data-sticky-atc-rating]');
    if (!ratingBlocks.length) return;

    this.observePageJudgeMeBadge();

    const show = this.hasValidJudgeMeReviews();
    ratingBlocks.forEach((block) => {
      block.hidden = !show;
      block.classList.toggle('is-visible', show);
    });
  }

  isMainSubmitVisible() {
    if (!this.mainSubmit) return true;

    const rect = this.mainSubmit.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.bottom > 0 && rect.top < viewportHeight;
  }

  updateVisibility() {
    const shouldShow = !this.isMainSubmitVisible();

    if (shouldShow === this.isVisible) return;

    this.isVisible = shouldShow;
    this.classList.toggle('is-visible', shouldShow);
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  handleStickySubmit(event) {
    event.preventDefault();

    if (this.stickySubmit.disabled || this.mainSubmit.disabled) return;

    this.mainSubmit.click();
    this.syncLoadingState();
  }

  handleVariantChange(variant) {
    if (!variant) return;

    if (variant.featured_media?.preview_image?.src && this.stickyImage) {
      const image = variant.featured_media.preview_image;
      const width = image.width ? Math.min(image.width, 120) : 120;
      this.stickyImage.src = `${image.src}${image.src.includes('?') ? '&' : '?'}width=${width}`;
      if (image.alt) this.stickyImage.alt = image.alt;
    }

    this.syncFromMainButton();
    requestAnimationFrame(() => this.updateVisibility());
  }

  syncFromMainButton() {
    if (!this.mainSubmit || !this.stickySubmit) return;

    const isDisabled =
      this.mainSubmit.disabled ||
      this.mainSubmit.getAttribute('aria-disabled') === 'true';

    this.stickySubmit.disabled = isDisabled;
    this.syncLoadingState();
    this.syncButtonText();
  }

  syncButtonText() {
    if (!this.mainSubmit || !this.stickyButtonText) return;

    const mainText = this.mainSubmit.querySelector('.product-form__submit-text');
    const soldOutMessage = this.mainSubmit.querySelector('.sold-out-message');

    if (soldOutMessage && !soldOutMessage.classList.contains('hidden')) {
      this.stickyButtonText.textContent = soldOutMessage.textContent.trim();
      return;
    }

    if (mainText) {
      this.stickyButtonText.innerHTML = mainText.innerHTML;
      return;
    }

    if (this.mainSubmit.disabled) {
      this.stickyButtonText.textContent = this.mainSubmit.textContent.trim();
    }
  }

  syncLoadingState() {
    if (!this.mainSubmit || !this.stickySubmit) return;

    const isLoading = this.mainSubmit.classList.contains('loading');
    this.stickySubmit.classList.toggle('loading', isLoading);
    this.syncButtonText();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
